import {
  DateRangePicker as NextUIDateRangePicker,
  DateRangePickerProps as NextUIDateRangePickerProps,
} from "@heroui/react";
import { CalendarDate, CalendarDateTime } from "@internationalized/date";
import {
  getCalendarClassNames,
  getDatePickerClassNames,
} from "ds/utils/styles";
import { CalendarIcon } from "lucide-react";
import {
  convertCalendarRangeToUnixRange,
  toCalendarDate,
} from "../../utils/date";

type Color = Extract<NextUIDateRangePickerProps["color"], "default">;
type LabelPlacement = Extract<
  NextUIDateRangePickerProps["labelPlacement"],
  "outside"
>;
type Variant = Extract<NextUIDateRangePickerProps["variant"], "bordered">;

export type DateRangePickerProps = Omit<
  NextUIDateRangePickerProps,
  | "color"
  | "labelPlacement"
  | "maxValue"
  | "minValue"
  | "selectorButtonPlacement"
  | "selectorIcon"
  | "value"
  | "variant"
> & {
  color?: Color;
  labelPlacement?: LabelPlacement;
  maxValue?: number;
  minValue?: number;
  name?: string;
  ref?: React.Ref<HTMLDivElement>;
  onChange?: (value: { end: number | null; start: number | null }) => void;
  value?: {
    end: number | null;
    start: number | null;
  };
  variant?: Variant;
};

export function DateRangePicker({ ref, ...props }: DateRangePickerProps) {
  const { granularity, maxValue, minValue, onChange, value, ...rest } = props;

  const calendarValue = value
    ? {
        end: toCalendarDate(value.end, granularity),
        start: toCalendarDate(value.start, granularity),
      }
    : undefined;

  const handleChange = (
    range: {
      end: CalendarDate | CalendarDateTime | null;
      start: CalendarDate | CalendarDateTime | null;
    } | null,
  ) => {
    if (!onChange) return;
    onChange(convertCalendarRangeToUnixRange(range, granularity));
  };

  return (
    <NextUIDateRangePicker
      {...rest}
      ref={ref}
      calendarProps={{
        classNames: getCalendarClassNames({ isRange: true }),
        visibleMonths: 2,
        weekdayStyle: "short",
      }}
      classNames={getDatePickerClassNames(props.size)}
      color="default"
      data-testid={props.name}
      labelPlacement="outside"
      maxValue={toCalendarDate(maxValue, granularity)}
      minValue={toCalendarDate(minValue, granularity)}
      selectorButtonPlacement="end"
      selectorIcon={<CalendarIcon size={16} />}
      value={calendarValue as NextUIDateRangePickerProps["value"]}
      variant="bordered"
      onChange={handleChange}
    />
  );
}

DateRangePicker.displayName = "RecareUI.DateRangePicker";
